import './mfeSharedRuntime';
import { ensureMfeUiShared } from './mfeSharedRuntime';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Router } from 'react-router-dom'
import { createBrowserHistory } from 'history'
import { patchHistoryForAdminShell } from './config/routePrefixes'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/lib/integration/react'
import { configurePersistor, configureStore } from './redux/store/store'
import { setSessionExpiredHandler } from './config/interceptors/session'
import Loading from './components/loaders/Loading'
import packageJson from '../package.json';
import { initNativeShell } from './native/init'
import { isNativeApp, getStoredTenant, setStoredTenant } from './native/platform'
import WorkspaceSetup from './native/WorkspaceSetup'

// CSS
import './assets/styles/index.css'
import 'react-toastify/dist/ReactToastify.css'
import localforage from 'localforage';


// Redux setup
const store = configureStore()
const persistor = configurePersistor(store)
// Dead session (expired JWT or first 401): log out once; App then redirects to /login and every other
// private call is cancelled client-side instead of reaching the API. 'LOGOUT_USER' is AuthActions'
// type, written literally so this entry file doesn't pull AuthActions (and config) into the main chunk.
setSessionExpiredHandler(() => store.dispatch({ type: 'LOGOUT_USER' }))
/** Host shell only — embedded stock MFEs use their own routers; `/operations/*` is never rewritten (see routePrefixes.js). */
const history = createBrowserHistory()
patchHistoryForAdminShell(history)
const root = ReactDOM.createRoot(document.getElementById('root'));

window.__REDUX_SHARED_STORE__= store;
window.__REDUX_SHARED_PERSISTOR__= persistor;
window.__HOST_HISTORY__ = history;

// Warm MUI/Emotion shared runtime in the background (needed before stock MFEs).
if (typeof window !== 'undefined') {
  const warm = () => {
    ensureMfeUiShared().catch(() => {});
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(warm, { timeout: 3000 });
  } else {
    window.setTimeout(warm, 1);
  }
}

// const {
//     REACT_APP_FCM_API_KEY,
//     REACT_APP_FCM_AUTH_DOMAIN,
//     REACT_APP_FCM_PROJECT_ID,
//     REACT_APP_FCM_STORAGE_BUCKET,
//     REACT_APP_FCM_MESSAGING_SENDER_ID,
//     REACT_APP_FCM_APP_ID,
//     REACT_APP_FCM_MEASUREMENT_ID,
//   } = process.env

// Used to build and pass env vars to sw for messaging
// const swUrl = `${process.env.PUBLIC_URL}/firebase-messaging-sw.js?REACT_APP_FCM_API_KEY=${REACT_APP_FCM_API_KEY}&REACT_APP_FCM_AUTH_DOMAIN=${REACT_APP_FCM_AUTH_DOMAIN}&REACT_APP_FCM_PROJECT_ID=${REACT_APP_FCM_PROJECT_ID}&REACT_APP_FCM_STORAGE_BUCKET=${REACT_APP_FCM_STORAGE_BUCKET}&REACT_APP_FCM_MESSAGING_SENDER_ID=${REACT_APP_FCM_MESSAGING_SENDER_ID}&REACT_APP_FCM_APP_ID=${REACT_APP_FCM_APP_ID}&REACT_APP_FCM_MEASUREMENT_ID=${REACT_APP_FCM_MEASUREMENT_ID}`;
// navigator.serviceWorker.register(swUrl);

// Android / iOS app (Capacitor): status bar, back button, downloads… — no-op on the web.
initNativeShell()

// The native app has no tenant subdomain: until a workspace is chosen, that is the only screen.
const needsWorkspace = isNativeApp() && !getStoredTenant()

root.render(
  needsWorkspace ? (
    <WorkspaceSetup />
  ) : (
    <React.Fragment>
      <Provider store={store}>
        <PersistGate loading={<Loading fullScreen />} persistor={persistor}>
          {/* Single router; push/replace are patched so bare paths get /admin (see patchHistoryForAdminShell). */}
          <Router history={history}>
            <App />
          </Router>
        </PersistGate>
      </Provider>
    </React.Fragment>
  )
);


function checkForNewVersionAndClearCache() {
  const newVersion = packageJson.version;

  let currentVersion = null;
  try {
    currentVersion = localStorage.getItem('appVersion');
  } catch (err) {
    // Storage unavailable (iOS Safari lockdown/private modes) — never reload over it
    return;
  }

  if (currentVersion === newVersion) return;

  // One reload per tab per version. Without this, a failed localStorage write
  // (iOS Safari quota/ITP eviction) makes every load clear + reload forever.
  let alreadyReloaded = false;
  try {
    alreadyReloaded = sessionStorage.getItem('appVersionReloadedTo') === newVersion;
  } catch (err) { /* ignore */ }

  // The native app's workspace survives an update (the user would otherwise have to type it again).
  const workspace = getStoredTenant();
  try { localStorage.clear(); } catch (err) { /* ignore */ }
  if (workspace) setStoredTenant(workspace);
  try { sessionStorage.clear(); } catch (err) { /* ignore */ }
  localforage.dropInstance().catch(function (err) {
    console.error('Error deleting database:', err);
  });

  try { localStorage.setItem('appVersion', newVersion); } catch (err) { /* ignore */ }
  try { sessionStorage.setItem('appVersionReloadedTo', newVersion); } catch (err) { /* ignore */ }

  if (!alreadyReloaded) {
    setTimeout(() => {
      window.location.reload();
    }, 0);
  }
}

checkForNewVersionAndClearCache();

window.onload = () => {
  checkForNewVersionAndClearCache();
};

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
